/**
 * Split Modal (Aktiesplit / Omvänd split)
 *
 * Modal for registering split and reverse split transactions.
 * - Split: Increases number of shares (e.g., 1:2 = each share becomes 2)
 * - Reverse Split: Decreases number of shares (e.g., 10:1 = 10 shares become 1)
 */

import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Divide, Layers, Info, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useRegisterSplit,
  useRegisterReverseSplit,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings } from "@/lib/api";

interface SplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

type SplitType = "split" | "reverse_split";

const splitSchema = z.object({
  splitType: z.enum(["split", "reverse_split"]),
  shareClassId: z.string().optional(), // If empty, applies to all share classes
  ratio: z.coerce
    .number()
    .int("Kvot måste vara ett heltal")
    .min(2, "Kvot måste vara minst 2"),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
});

type SplitFormData = z.infer<typeof splitSchema>;

export function SplitModal({ isOpen, onClose, companyId }: SplitModalProps) {
  const [splitType, setSplitType] = useState<SplitType>("split");
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerSplit = useRegisterSplit();
  const registerReverseSplit = useRegisterReverseSplit();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SplitFormData>({
    resolver: zodResolver(splitSchema),
    defaultValues: {
      splitType: "split",
      shareClassId: "",
      ratio: 2,
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareClassId = watch("shareClassId");
  const watchRatio = watch("ratio");

  // Calculate totals
  const totals = useMemo(() => {
    const activeShareholders = shareholders.filter(
      (s: ShareholderWithHoldings) => s.isActive
    );

    let currentShares = 0;
    let newShares = 0;

    if (watchShareClassId) {
      // Specific share class
      const shareClass = shareClasses.find(
        (sc: ShareClass) => sc.id === watchShareClassId
      );
      if (shareClass) {
        currentShares = shareClass.totalShares;
        if (splitType === "split") {
          newShares = currentShares * (watchRatio || 1);
        } else {
          newShares = Math.floor(currentShares / (watchRatio || 1));
        }
      }
    } else {
      // All share classes
      currentShares = shareClasses.reduce(
        (sum: number, sc: ShareClass) => sum + sc.totalShares,
        0
      );
      if (splitType === "split") {
        newShares = currentShares * (watchRatio || 1);
      } else {
        newShares = Math.floor(currentShares / (watchRatio || 1));
      }
    }

    // Check for fractional shares in reverse split
    const hasFractional =
      splitType === "reverse_split" && currentShares % (watchRatio || 1) !== 0;

    return { currentShares, newShares, hasFractional, activeShareholders };
  }, [
    shareholders,
    shareClasses,
    watchShareClassId,
    watchRatio,
    splitType,
  ]);

  // Preview per shareholder for reverse split (to show fractional issues)
  const shareholderPreview = useMemo(() => {
    if (splitType !== "reverse_split" || !watchRatio) return [];

    return totals.activeShareholders
      .map((s: ShareholderWithHoldings) => {
        let currentShares = 0;
        if (watchShareClassId) {
          const holding = s.holdings.find(
            (h) => h.shareClass.id === watchShareClassId
          );
          currentShares = holding?.totalShares || 0;
        } else {
          currentShares = s.totalShares;
        }

        const newShares = Math.floor(currentShares / watchRatio);
        const remainder = currentShares % watchRatio;

        return {
          id: s.id,
          name: s.name,
          currentShares,
          newShares,
          remainder: remainder > 0 ? remainder : null,
        };
      })
      .filter((s) => s.currentShares > 0);
  }, [totals.activeShareholders, watchShareClassId, watchRatio, splitType]);

  const hasRemainderIssues = useMemo(() => {
    return shareholderPreview.some((s) => s.remainder !== null);
  }, [shareholderPreview]);

  const handleSplitTypeChange = (type: SplitType) => {
    setSplitType(type);
    setValue("splitType", type);
  };

  const onSubmit = async (data: SplitFormData) => {
    try {
      const baseData = {
        shareClassId: data.shareClassId || undefined,
        ratio: data.ratio,
        transactionDate: data.transactionDate,
        notes: data.notes,
      };

      if (data.splitType === "split") {
        await registerSplit.mutateAsync({ companyId, data: baseData });
        toast.success(
          `Aktiesplit ${data.ratio}:1 registrerad - ${totals.newShares.toLocaleString("sv-SE")} aktier totalt`
        );
      } else {
        await registerReverseSplit.mutateAsync({ companyId, data: baseData });
        toast.success(
          `Omvänd split 1:${data.ratio} registrerad - ${totals.newShares.toLocaleString("sv-SE")} aktier totalt`
        );
      }
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera split";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setSplitType("split");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={splitType === "split" ? "Aktiesplit" : "Omvänd aktiesplit"}
      size="lg"
    >
      <div className="space-y-6">
        {/* Split Type Toggle */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleSplitTypeChange("split")}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              splitType === "split"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  splitType === "split"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <Layers size={20} />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    splitType === "split" ? "text-blue-900" : "text-gray-900"
                  }`}
                >
                  Aktiesplit
                </p>
                <p className="text-xs text-gray-500">Ökar antal aktier</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleSplitTypeChange("reverse_split")}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              splitType === "reverse_split"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  splitType === "reverse_split"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <Divide size={20} />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    splitType === "reverse_split"
                      ? "text-blue-900"
                      : "text-gray-900"
                  }`}
                >
                  Omvänd split
                </p>
                <p className="text-xs text-gray-500">Minskar antal aktier</p>
              </div>
            </div>
          </button>
        </div>

        {/* Info box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-blue-900">
                {splitType === "split"
                  ? "Vad är aktiesplit?"
                  : "Vad är omvänd split?"}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {splitType === "split"
                  ? "Vid aktiesplit delas varje aktie upp i flera aktier. Aktiekapitalet förblir oförändrat men antalet aktier ökar. Exempel: 2:1 = varje aktie blir 2 aktier."
                  : "Vid omvänd split slås flera aktier samman till färre. Aktiekapitalet förblir oförändrat men antalet aktier minskar. Exempel: 1:10 = 10 aktier blir 1 aktie."}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <input type="hidden" {...register("splitType")} />

          {/* Share Class (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag (valfritt)
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Alla aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")}{" "}
                  aktier
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Lämna tomt för att applicera på alla aktieslag
            </p>
          </div>

          {/* Ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {splitType === "split" ? "Splitkvot (X:1)" : "Sammanslagningskvot (1:X)"}{" "}
              *
            </label>
            <input
              type="number"
              {...register("ratio")}
              className="input"
              min={2}
            />
            <p className="text-xs text-gray-500 mt-1">
              {splitType === "split"
                ? `Exempel: 2 = varje aktie blir 2 aktier (2:1 split)`
                : `Exempel: 10 = 10 aktier slås samman till 1 aktie (1:10)`}
            </p>
            {errors.ratio && (
              <p className="text-sm text-red-600 mt-1">{errors.ratio.message}</p>
            )}
          </div>

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Preview */}
          {totals.currentShares > 0 && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Info className="text-gray-600" size={16} />
                <p className="font-medium text-gray-900">Resultat</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Nuvarande antal aktier:</p>
                  <p className="font-medium text-gray-900">
                    {totals.currentShares.toLocaleString("sv-SE")} st
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Efter {splitType === "split" ? "split" : "omvänd split"}:</p>
                  <p className="font-medium text-green-700">
                    {totals.newShares.toLocaleString("sv-SE")} st
                  </p>
                </div>
              </div>

              {splitType === "split" && (
                <p className="text-sm text-gray-600 mt-2">
                  Multiplikation: {watchRatio}x
                </p>
              )}
              {splitType === "reverse_split" && (
                <p className="text-sm text-gray-600 mt-2">
                  Division: ÷{watchRatio}
                </p>
              )}
            </div>
          )}

          {/* Reverse split shareholder preview (for fractional warnings) */}
          {splitType === "reverse_split" && hasRemainderIssues && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle
                  className="text-amber-600 mt-0.5 flex-shrink-0"
                  size={18}
                />
                <div>
                  <p className="font-medium text-amber-900">
                    Observera: Restaktier uppstår
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Vissa aktieägare har inte jämnt delbart antal aktier. Dessa
                    restaktier måste hanteras (t.ex. genom inlösen eller sammanslagning).
                  </p>
                </div>
              </div>

              <div className="space-y-2 max-h-32 overflow-y-auto">
                {shareholderPreview
                  .filter((s) => s.remainder !== null)
                  .map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between text-sm py-1 border-b border-amber-100 last:border-0"
                    >
                      <span className="text-amber-800">{s.name}</span>
                      <span className="text-amber-900">
                        {s.currentShares} st → {s.newShares} st{" "}
                        <span className="text-amber-600">
                          (+{s.remainder} rest)
                        </span>
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar..."
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Krav:</strong> Beslut om aktiesplit fattas av bolagsstämman
              och kräver ändring av bolagsordningen. Registrering hos Bolagsverket
              krävs.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || totals.currentShares === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : splitType === "split" ? (
                "Registrera aktiesplit"
              ) : (
                "Registrera omvänd split"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
